export interface StreamingPreviewSections {
  completedHtml: string
  liveText: string
}

const VOID_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
])

const SUPPRESSED_BLOCK_PATTERN = /<(script|style)\b[^>]*>[\s\S]*?(<\/\1\s*>|$)/gi
const HTML_TAG_PATTERN = /<\/?[a-zA-Z][^>]*>/g
const INCOMPLETE_HTML_TAG_TAIL_PATTERN = /<\/?[a-zA-Z][^>]*$/g

const NAMED_HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
}

function decodeHtmlEntities(input: string): string {
  const toCodePointChar = (codePoint: number): string | null => {
    if (!Number.isInteger(codePoint) || codePoint < 0 || codePoint > 0x10ffff) {
      return null
    }

    try {
      return String.fromCodePoint(codePoint)
    } catch {
      return null
    }
  }

  return input.replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g, (match, entityBody: string) => {
    if (entityBody.startsWith('#x') || entityBody.startsWith('#X')) {
      const codePoint = Number.parseInt(entityBody.slice(2), 16)
      return toCodePointChar(codePoint) ?? match
    }

    if (entityBody.startsWith('#')) {
      const codePoint = Number.parseInt(entityBody.slice(1), 10)
      return toCodePointChar(codePoint) ?? match
    }

    return NAMED_HTML_ENTITIES[entityBody.toLowerCase()] ?? match
  })
}

function decodePotentialEncodedHtml(input: string): string {
  if (!/&lt;/i.test(input)) {
    return input
  }

  if (!/&lt;(?:\/|!|[a-zA-Z])/i.test(input)) {
    return input
  }

  return decodeHtmlEntities(input)
}

interface HtmlBoundaryScan {
  // Last index where no tags remain open (fully structurally complete HTML).
  stableBoundary: number
  // Last index right after a completed </td> or </th> while a <table> is still open.
  // Lets us render tables cell-by-cell instead of waiting for </tr> or </table>.
  cellBoundary: number
  // Closing tags needed to make the prefix up to cellBoundary valid HTML.
  cellClosingTags: string
  // True while the stream ends inside an unclosed <table> (including partial rows).
  hasOpenTable: boolean
}

function findStableHtmlBoundary(input: string): HtmlBoundaryScan {
  const openTags: string[] = []
  let inTag = false
  let tagBuffer = ''
  let quote: '"' | "'" | null = null
  let stableBoundary = 0
  let cellBoundary = 0
  let cellClosingTags = ''

  const synthesizeClosingTags = () =>
    openTags
      .map((tag) => `</${tag}>`)
      .reverse()
      .join('')

  const processTag = (rawTag: string): { closedCell: boolean } => {
    const tagMatch = /^<\s*(\/?)\s*([a-zA-Z0-9:-]+)/.exec(rawTag)
    if (!tagMatch) return { closedCell: false }

    const isClosing = tagMatch[1] === '/'
    const tagName = tagMatch[2].toLowerCase()
    const isSelfClosing = /\/\s*>$/.test(rawTag)
    const isVoidTag = VOID_TAGS.has(tagName)

    if (isClosing) {
      for (let index = openTags.length - 1; index >= 0; index -= 1) {
        if (openTags[index] !== tagName) {
          continue
        }

        openTags.splice(index)
        break
      }
      return { closedCell: tagName === 'td' || tagName === 'th' }
    }

    if (!isSelfClosing && !isVoidTag) {
      openTags.push(tagName)
    }

    return { closedCell: false }
  }

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index]

    if (inTag) {
      tagBuffer += char

      if (quote) {
        if (char === quote) {
          quote = null
        }
        continue
      }

      if (char === '"' || char === "'") {
        quote = char
        continue
      }

      if (char === '>') {
        const { closedCell } = processTag(tagBuffer)
        inTag = false
        tagBuffer = ''

        if (openTags.length === 0) {
          stableBoundary = index + 1
        } else if (closedCell && openTags.includes('table')) {
          cellBoundary = index + 1
          cellClosingTags = synthesizeClosingTags()
        }
      }

      continue
    }

    const nextChar = input[index + 1]
    const looksLikeTagStart = nextChar !== undefined && /[a-zA-Z!/]/.test(nextChar)

    if (char === '<' && looksLikeTagStart) {
      inTag = true
      tagBuffer = '<'
      quote = null
      continue
    }

    if (openTags.length === 0) {
      stableBoundary = index + 1
    }
  }

  return { stableBoundary, cellBoundary, cellClosingTags, hasOpenTable: openTags.includes('table') }
}

function htmlToText(input: string): string {
  if (!input) return ''

  return decodeHtmlEntities(
    input
      .replace(SUPPRESSED_BLOCK_PATTERN, ' ')
      .replace(HTML_TAG_PATTERN, ' ')
      .replace(INCOMPLETE_HTML_TAG_TAIL_PATTERN, ' ')
  )
    .replace(/\s+/g, ' ')
    .trim()
}

export function splitStreamingPreviewContent(rawStreamContent: string): StreamingPreviewSections {
  const normalized = decodePotentialEncodedHtml(rawStreamContent)
  const { stableBoundary, cellBoundary, cellClosingTags, hasOpenTable } =
    findStableHtmlBoundary(normalized)

  // Prefer the cell boundary while a table is still streaming so completed cells
  // render immediately instead of waiting for </tr> or </table>.
  const useCellBoundary = cellBoundary > stableBoundary
  const boundary = useCellBoundary ? cellBoundary : stableBoundary
  const closingTags = useCellBoundary ? cellClosingTags : ''

  const completedHtml = (
    normalized.slice(0, boundary).replace(SUPPRESSED_BLOCK_PATTERN, '') + closingTags
  ).trim()
  const pendingChunk = normalized.slice(boundary)
  // Tables stream cell-by-cell in completedHtml only - never expose raw cell text in liveText.
  const liveText = hasOpenTable ? '' : htmlToText(pendingChunk)

  return {
    completedHtml,
    liveText,
  }
}
